import { loadAndCacheImage } from "@cornerstonejs/core/loaders/imageLoader";
import { getViewport } from "./CornerstoneViewportService";
import { CONSTRUCTABLE_MODALITIES } from "../../../constants/Constants";
import { metaData } from "@cornerstonejs/core";
import { sortSeries } from "./sortSeriesDataByPosition";
import { getThumbnailViewport } from "./CornerstoneThumbnailService";

const reconstructionIssues = {
  MISSING_FRAMES: "missingframes",
  IRREGULAR_SPACING: "irregularspacing",
};
const spacingTolerance = 0.2;
const iopTolerance = 0.01;
export const createSeriesData = async (imageIds) => {
  var seriesData = [];

  for(let i=0 ; i<imageIds.length; i++) {

    const image = await loadAndCacheImage(imageIds[i]);
    const multiFrame = metaData.get("multiframeModule", image.imageId);
    const imagePlane = metaData.get("imagePlaneModule", image.imageId);
    const imagePixel = metaData.get("imagePixelModule", image.imageId);
    let obj = {
      // imageIds: "asdbasgdghaskjdg"
      imageId: image.imageId,
      NumberOfFrames: multiFrame.NumberOfFrames,
      imagePlane: imagePlane,
      imagePixel: imagePixel,
      PerFrameFunctionalInformation: multiFrame.PerFrameFunctionalInformation,
      SharedFunctionalInformation: multiFrame.SharedFunctionalInformation,
    };
    seriesData.push(obj);
  }
  return seriesData;
};

export const isReconstructable = async (
  viewportIndex, viewportType
) => {

  let viewport;
  if(viewportType === "viewport") {
    viewport = await getViewport(viewportIndex)
  } else {
    viewport = await getThumbnailViewport(viewportIndex);
  }
  const imageIds = viewport.imageIds;

  if (!imageIds.length) {
    return { value: false };
  }

  const seriesData = await createSeriesData(imageIds);
  const firstInstance = seriesData[0];
  const isMultiFrame = firstInstance.NumberOfFrames > 1;
  const modality = viewport.modality;
  //   const isMultiFrame = false;
  if (!CONSTRUCTABLE_MODALITIES.includes(modality)) {
    return { value: false };
  }

  if (!isMultiFrame && seriesData.length === 1) {
    return { value: false };
  }

  if (
    !seriesData.every((instance) => !!instance.imagePlane.imagePositionPatient)
  ) {
    return { value: false };
  }
  const sortedSeries = sortSeries(seriesData);
  if (isMultiFrame) {
    return processMultiframe(sortedSeries[0]);
  } else {
    return processSingleframe(sortedSeries);
  }
};

const processMultiframe = (multiFrameInstance) => {
  const { PerFrameFunctionalGroupsSequence, SharedFunctionalGroupsSequence } =
    multiFrameInstance;

  if (
    !SharedFunctionalGroupsSequence ||
    !SharedFunctionalGroupsSequence[0].PixelMeasuresSequence
  ) {
    return { value: false };
  }

  const { PlaneOrientationSequence: sharedOrientation } =
    SharedFunctionalGroupsSequence;

  if (!sharedOrientation) {
    const { PlaneOrientationSequence: firstOrientation } =
      PerFrameFunctionalGroupsSequence[0];

    if (!firstOrientation) {
      return { value: false };
    }
  }

  const frame0 = PerFrameFunctionalGroupsSequence[0];
  const firstPosition =
    frame0.PlanePositionSequence || frame0.CTPositionSequence;
  if (!firstPosition) {
    return { value: false };
  }
  // TODO - check spacing consistency

  return { value: true };
};

function processSingleframe(instances) {
  const firstImage = instances[0];
  const firstImageRows = toNumber(firstImage.imagePixel.rows);
  const firstImageColumns = toNumber(firstImage.imagePixel.columns);
  const firstImageSamplesPerPixel = toNumber(
    firstImage.imagePixel.samplesPerPixel
  );
  const firstImageOrientationPatient = toNumber(
    firstImage.imagePlane.imageOrientationPatient
  );
  const firstImagePositionPatient = toNumber(
    firstImage.imagePlane.imagePositionPatient
  );

  // Can't reconstruct if we:
  // -- Have a different dimensions within a displaySet.
  // -- Have a different number of components within a displaySet.
  // -- Have different orientations within a displaySet.
  for (let i = 1; i < instances.length; i++) {
    const instance = instances[i];
    const Rows = instance.imagePixel.rows;
    const Columns = instance.imagePixel.columns;
    const SamplesPerPixel = instance.imagePixel.samplesPerPixel;
    const ImageOrientationPatient = instance.imagePlane.imageOrientationPatient;

    const imageOrientationPatient = toNumber(ImageOrientationPatient);

    if (
      Rows !== firstImageRows ||
      Columns !== firstImageColumns ||
      SamplesPerPixel !== firstImageSamplesPerPixel ||
      !isSameOrientation(imageOrientationPatient, firstImageOrientationPatient)
    ) {
      return { value: false };
    }
  }

  let missingFrames = 0;

  // Check if frame spacing is approximately equal within a spacingTolerance.
  // If spacing is on a uniform grid but we are missing frames,
  // Allow reconstruction, but pass back the number of missing frames.
  if (instances.length > 2) {
    const lastIpp = toNumber(
      instances[instances.length - 1].imagePlane.imagePositionPatient
    );

    // We can't reconstruct if we are missing ImagePositionPatient values
    if (!firstImagePositionPatient || !lastIpp) {
      return { value: false };
    }

    const averageSpacingBetweenFrames =
      getPerpendicularDistance(firstImagePositionPatient, lastIpp) /
      (instances.length - 1);

    let previousImagePositionPatient = firstImagePositionPatient;

    for (let i = 1; i < instances.length; i++) {
      const instance = instances[i];
      // Todo: get metadata from OHIF.MetadataProvider
      const imagePositionPatient = toNumber(
        instance.imagePlane.imagePositionPatient
      );

      const spacingBetweenFrames = getPerpendicularDistance(
        imagePositionPatient,
        previousImagePositionPatient
      );
      const spacingIssue = getSpacingIssue(
        spacingBetweenFrames,
        averageSpacingBetweenFrames
      );

      if (spacingIssue) {
        const issue = spacingIssue.issue;

        if (issue === reconstructionIssues.MISSING_FRAMES) {
          missingFrames += spacingIssue.missingFrames;
        } else if (issue === reconstructionIssues.IRREGULAR_SPACING) {
          return { value: false };
        }
      }

      previousImagePositionPatient = imagePositionPatient;
    }
  }

  return { value: true, missingFrames };
}

const toNumber = (val) => {
  if (Array.isArray(val)) {
    return val.map((v) => (v !== undefined ? Number(v) : v));
  } else {
    return val !== undefined ? Number(val) : val;
  }
};

const isSameOrientation = (iop1, iop2) => {
  if (iop1 === undefined || !iop2 === undefined) {
    return;
  }

  return (
    Math.abs(iop1[0] - iop2[0]) < iopTolerance &&
    Math.abs(iop1[1] - iop2[1]) < iopTolerance &&
    Math.abs(iop1[2] - iop2[2]) < iopTolerance
  );
};

const getPerpendicularDistance = (a, b) => {
  return Math.sqrt(
    Math.pow(a[0] - b[0], 2) +
      Math.pow(a[1] - b[1], 2) +
      Math.pow(a[2] - b[2], 2)
  );
};

const getSpacingIssue = (spacing, averageSpacing) => {
  const equalWithinTolerance =
    Math.abs(spacing - averageSpacing) < averageSpacing * spacingTolerance;

  if (equalWithinTolerance) {
    return;
  }

  const multipleOfAverageSpacing = spacing / averageSpacing;

  const numberOfSpacings = Math.round(multipleOfAverageSpacing);

  const errorForEachSpacing =
    Math.abs(spacing - numberOfSpacings * averageSpacing) / numberOfSpacings;

  if (errorForEachSpacing < spacingTolerance * averageSpacing) {
    return {
      issue: reconstructionIssues.MISSING_FRAMES,
      missingFrames: numberOfSpacings - 1,
    };
  }

  return { issue: reconstructionIssues.IRREGULAR_SPACING };
};
